import { liquidInfo } from './measurements.js';
/**
 * Разбор свободного текста вида «100 гр овсянки, 20 г творога, банан».
 *
 * Правила, а не модель: список продуктов конечен, а формулировки в дневнике
 * однотипны, поэтому регулярки дают предсказуемый результат, который можно
 * чинить точечно. Всё, что не разобралось, возвращается наверх, а не молча
 * пропадает из дневника.
 */
/** Сколько граммов в бытовой мере. Значения усреднённые. */
const MEASURES = {
    tablespoon: 15,
    teaspoon: 5,
    glass: 250,
    cup: 240,
};
/**
 * Окончание слова. Именно \p{L}, а не \w: \w в JavaScript - это ASCII, и на
 * нём «грама» и «столовая ложка» не разбирались бы вовсе.
 */
const END = String.raw `[\p{L}]*`;
// Порядок важен: «кг» должен проверяться раньше «г», иначе «2 кг» станет «2 г».
const UNITS = [
    { re: new RegExp(String.raw `кг|килограмм${END}`, 'u'), grams: 1000, kind: 'mass' },
    { re: new RegExp(String.raw `мл|миллилитр${END}|ml`, 'u'), grams: 1, kind: 'volume' },
    { re: new RegExp(String.raw `л(?:итр${END})?|l`, 'u'), grams: 1000, kind: 'volume' },
    { re: new RegExp(String.raw `г(?:р(?:амм?${END})?)?`, 'u'), grams: 1, kind: 'mass' },
    {
        re: new RegExp(String.raw `ст\.?\s*л\.?|столов${END}\s+ложк${END}`, 'u'),
        grams: MEASURES.tablespoon,
        kind: 'household',
    },
    {
        re: new RegExp(String.raw `ч\.?\s*л\.?|чайн${END}\s+ложк${END}`, 'u'),
        grams: MEASURES.teaspoon,
        kind: 'household',
    },
    { re: new RegExp(String.raw `стакан${END}|кружк${END}|чашк${END}`, 'u'), grams: MEASURES.glass, kind: 'household' },
    { re: new RegExp(String.raw `шт\.?(?:ук${END})?`, 'u'), grams: null, kind: 'piece' },
    { re: new RegExp(String.raw `порци${END}`, 'u'), grams: null, kind: 'serving' },
];
const UNIT_PATTERN = UNITS.map((u) => u.re.source).join('|');
/** Число с точкой или запятой: «1,5», «0.5», «200». */
const NUMBER = String.raw `\d+(?:[.,]\d+)?`;
/** «200 гр», «200гр», «1,5 л» - число, за ним мера. */
const NUMBER_UNIT = new RegExp(String.raw `(?<![\p{L}\d])(${NUMBER})\s*(${UNIT_PATTERN})(?![\p{L}])`, 'iu');
/** «столовая ложка мёда» - мера без числа, считаем за одну. */
const BARE_UNIT = new RegExp(String.raw `(?<![\p{L}\d])(${UNIT_PATTERN})(?![\p{L}])`, 'iu');
/** Голое число в начале: «2 яйца», «3 банана». */
const LEADING_COUNT = new RegExp(String.raw `^\s*(${NUMBER})\s+(?=\p{L})`, 'u');
/** Предлоги и союзы, которые остаются в названии после вырезания меры. */
const STOP_WORDS = new RegExp(String.raw `^(?:и|с|со|из|на|в|во|по|около|примерно|где-?то|штук${END}|шт\.?)\s+`, 'iu');
function toNumber(value) {
    return Number(value.replace(',', '.'));
}
/** Убирает служебные слова и мусор по краям названия. */
function cleanQuery(text) {
    let out = text.replace(/[()"'«».]/g, ' ').replace(/\s+/g, ' ').trim();
    // Предлогов может быть несколько подряд: «и с творога»
    let previous;
    do {
        previous = out;
        out = out.replace(STOP_WORDS, '').trim();
    } while (out !== previous);
    return out;
}
/**
 * Делит текст на отдельные продукты.
 *
 * Режем только по запятым, точкам с запятой и переводам строки. По союзу «и»
 * резать нельзя: «салат с листьями и помидором» - это одна позиция, а не две.
 */
export function splitItems(text) {
    return text
        .split(/(?<!\d),|,(?!\d)|[;\n]+/)
        .map((part) => part.trim())
        .filter((part) => part.length > 1);
}
/** Достаёт из фрагмента количество и название продукта. */
export function parseFragment(raw) {
    const trimmed = raw.trim();
    const numberUnit = NUMBER_UNIT.exec(trimmed);
    if (numberUnit) {
        const amount = toNumber(numberUnit[1]);
        const rule = matchUnit(numberUnit[2]);
        const rest = cleanQuery(trimmed.replace(numberUnit[0], ' '));
        return {
            raw: trimmed,
            query: rest,
            grams: isVolume(rule, rest) ? null : rule?.grams != null ? amount * rule.grams : null,
            volumeMl: isVolume(rule, rest) && rule?.grams != null ? amount * rule.grams : null,
            estimatedMeasure: rule?.kind === 'household' && !isVolume(rule, rest),
            count: rule?.grams == null ? amount : null,
        };
    }
    const bareUnit = BARE_UNIT.exec(trimmed);
    if (bareUnit) {
        const rule = matchUnit(bareUnit[1]);
        const rest = cleanQuery(trimmed.replace(bareUnit[0], ' '));
        // Мера без числа осмысленна, только если после неё осталось название
        if (rest) {
            return {
                raw: trimmed,
                query: rest,
                grams: isVolume(rule, rest) ? null : rule?.grams ?? null,
                volumeMl: isVolume(rule, rest) ? rule?.grams ?? null : null,
                estimatedMeasure: rule?.kind === 'household' && !isVolume(rule, rest),
                count: rule?.grams == null ? 1 : null,
            };
        }
    }
    const leading = LEADING_COUNT.exec(trimmed);
    if (leading) {
        return {
            raw: trimmed,
            query: cleanQuery(trimmed.replace(leading[0], ' ')),
            grams: null,
            count: toNumber(leading[1]),
        };
    }
    return { raw: trimmed, query: cleanQuery(trimmed), grams: null, count: null };
}
function matchUnit(token) {
    const value = token.toLowerCase();
    return UNITS.find((u) => new RegExp(`^(?:${u.re.source})$`, 'iu').test(value));
}
function isVolume(rule, name) {
    return rule?.kind === 'volume' || (rule?.kind === 'household' && liquidInfo(name).liquid);
}
/** Порция по умолчанию, когда у продукта не указан размер. */
export const DEFAULT_PORTION_G = 100;
/**
 * Итоговый вес позиции.
 *
 * Явные граммы важнее всего; «2 шт» умножает порцию продукта; если не сказано
 * ничего, берём одну порцию - так «яблоко» превращается в осмысленный вес.
 */
export function resolveGrams(fragment, servingSizeG) {
    if (fragment.grams != null)
        return fragment.grams;
    if (fragment.volumeMl != null)
        throw new Error('Для миллилитров используйте resolvePortion с плотностью продукта');
    const count = fragment.count ?? 1;
    // Порядок важен: вес штуки от модели точнее порции продукта, а порция
    // продукта точнее общего запаса. Запас в 100 г за штуку - последний рубеж
    // и самый грубый: именно он превращал два ломтика хлеба в 200 граммов.
    const piece = fragment.pieceGrams ?? servingSizeG ?? DEFAULT_PORTION_G;
    return Math.round(piece * count * 10) / 10;
}
//# sourceMappingURL=food-text.js.map